import {GROUND_BACKGROUNDS} from '../groundBackgrounds';

/**
 * СТАЯ ГРИФОВ В ВОСХОДЯЩЕМ ПОТОКЕ — эффект костяной подложки (bone_1).
 *
 * Над серой растрескавшейся равниной кружит стая из нескольких грифов: все
 * ходят в одну сторону по широким кругам вокруг общей середины, а сама
 * середина медленно сносится ветром матча (см. utils/effects/biome/vultureKettle).
 * Грифы слетаются по одному, кружат с полминуты и так же по одному уходят.
 *
 * Гриф — картинка владельца игры (FIELD_BIOME_VULTURE_IMAGE в
 * configs/fieldBiomeEffects): вид сверху, крылья раскинуты. Здесь — только
 * числа полета и размеры; сама картинка, ее путь и размеры кадра — там.
 *
 * На этом поле уже есть стаи птиц и орел над горой, поэтому грифы на них не
 * похожи: их несколько на одном широком круге (орел — один и вплотную к горе),
 * они не машут крыльями вовсе, только медленно кренятся, держатся над открытой
 * равниной, и под каждым по земле бежит тень. Иногда, если камера видит
 * гигантский череп у левого края пейзажа, стая повисает над ним.
 */
export const VULTURE_KETTLE_KIND = 'vultureKettle';

export const VULTURE_KETTLE_EFFECT = Object.freeze({
  kind: VULTURE_KETTLE_KIND,
  backgrounds: Object.freeze({
    [GROUND_BACKGROUNDS.BONE_1]: Object.freeze({
      // Первая стая — вскоре после начала; следующая — через паузу от появления
      // предыдущей. Пауза длиннее жизни стаи: двух стай разом не бывает, и между
      // стаями небо над равниной с полминуты пустое
      firstDelayMs: {min: 4000, max: 12000},
      intervalMs: {min: 62000, max: 92000},
      maxGroups: 1,
      // Сколько стая живет: от появления первого грифа до ухода последнего.
      // Не меньше, чем нужно самой большой стае: семь грифов слетаются и
      // разлетаются по одному (до 15 секунд на сбор и столько же на уход), и
      // последнему из них еще надо успеть сделать круг
      lifeMs: {min: 42000, max: 50000},
      // Грифов в стае — от пары до семи (числа владельца игры: стаи бывают и
      // маленькими, и большими)
      birdCount: {min: 2, max: 7},
      bird: {
        // Размах крыльев обычного грифа, размеров ячейки. Гриф заметно крупнее
        // орла над горой (0.54) и втрое крупнее птицы из стаи: мельче — на
        // телефоне не найти, крупнее — закрывал бы значки отрядов, над
        // которыми кружит
        wingspanFactor: 0.80,
        // У каждого грифа свой размер — доля обычного размаха: одинаковые птицы
        // читались бы копиями одной картинки. Самый крупный гриф в полтора раза
        // крупнее самого мелкого: шире разброс — мелкий читался бы птицей другой
        // породы, а крупный (сейчас его размах — чуть больше половины ширины
        // ячейки поля) закрывал бы значки отрядов
        scale: {min: 0.8, max: 1.2},
        // Размеры в стае разведены по ступеням от мелкого к крупному, по грифу
        // на ступень: в любой стае есть и мелкий гриф, и крупный. Где на
        // ступени размер грифа (доля ее ширины): не у края — иначе грифы
        // соседних ступеней сошлись бы в размере
        scaleStepShare: {min: 0.2, max: 0.8},
        // Краска спрайта птицы: белая — картинка рисуется своими красками, и
        // не просвечивает
        tint: 0xffffff,
        alpha: 1,
        // Грифы слетаются и уходят по одному: пауза между соседними и время, за
        // которое гриф проступает и исчезает
        staggerMs: {min: 1500, max: 2500},
        fadeMs: 1800,
        // Гриф приходит по спирали снаружи и так же уходит: за столько
        // миллисекунд его круг сужается от approachScale своих радиусов до одного
        approachMs: 5000,
        approachScale: 1.5,
        // Крыльями гриф не машет — только медленно кренится: размах на виде
        // сверху укорачивается на эту долю и возвращается
        bankDepth: 0.08,
        bankPeriodMs: {min: 3000, max: 4000},
      },
      orbit: {
        // Радиус круга грифа, размеров ячейки: круг широкий — до двух ячеек
        // поля (у орла над горой — 0.4–0.68)
        radiusFactor: {min: 1.6, max: 3.9},
        // У каждого грифа своя ступень радиуса; где на ступени его круг (доля
        // ее ширины): не у края — соседние круги не сливаются
        stepShare: {min: 0.3, max: 0.7},
        // Время оборота: на ближнем круге — min, на дальнем — max, плюс
        // небольшой разброс, чтобы грифы не шли строем
        periodMs: {min: 9000, max: 13000},
        periodJitterShare: 0.06,
        // Круг «дышит»: радиус гуляет на эту долю
        breatheShare: 0.1,
        breathePeriodMs: {min: 7000, max: 11000},
        // Круги не точно вокруг одной точки: сдвиг середины, размеров ячейки
        centreJitterFactor: 0.45,
        // В начале грифы расставлены по кругу, но не поровну: сдвиг от ровного
        // места, доля расстояния между соседями
        phaseJitterShare: 0.3,
      },
      // Поток сносит ветром матча: скорость середины стаи (размеров ячейки в
      // секунду) и отклонение сноса от ветра (градусы). Стая не висит полминуты
      // над одними и теми же ячейками
      drift: {
        speedFactor: {min: 0.07, max: 0.13},
        spreadDeg: 30,
      },
      // Тень грифа на земле — та же картинка птицы, закрашенная черным: у тени
      // силуэт самой птицы, того же размера и в том же повороте. Лежит под
      // дымкой ячеек и жетонами. Бледная: плотнее 0.4 силуэт читается второй
      // птицей, а слабее 0.2 на пятнистой глине тень не видна. Смещена вниз и
      // чуть вправо, как тени гор на картинке и тени облаков
      shadow: {
        tint: 0x000000,
        alpha: 0.3,
        offsetFactor: {x: 0.2, y: 1.15},
      },
      // Серая равнина на картинке пейзажа (доли ее стороны) — здесь лежит поле
      plain: {u: 0.5, v: 0.5, radiusU: 0.235, radiusV: 0.255},
      // Середина стаи — не дальше этой доли полуосей от середины равнины
      plainInnerShare: 0.8,
      // ...и не ближе стольких размеров ячейки к краю кадра: круг широкий, и у
      // самого края половина стаи кружила бы за кадром
      viewInsetFactor: 3.5,
      // Гигантский череп у левого края пейзажа. Поле до него не доходит, и на
      // телефоне его не видно: стая повисает над ним, только когда камера на
      // него смотрит, и то не каждый раз
      skull: {
        u: 0.1374,
        v: 0.4767,
        // Какая доля стай собирается над черепом, когда он в кадре
        chance: 0.34,
        // Середина стаи — не дальше стольких размеров ячейки от середины черепа
        jitterFactor: 0.6,
        // Над черепом поток почти стоит: доля обычной скорости сноса
        driftShare: 0.3,
      },
    }),
  }),
});
